/**
 * DailyHoursCell — iter133
 * ────────────────────────────────────────────────────────────────────────
 * Per-mechanic-per-day cell for the Daily Entry Monthly Grid.
 *
 * Two-line display when there is auto-computed data:
 *   ┌──────────────────┐
 *   │  5.5 / 7.5       │  ← actual / allocated (bold, large)
 *   │  73%   override  │  ← variance % + tiny edit pill
 *   └──────────────────┘
 *
 * Click anywhere on the cell to enter manual override mode (Input). The
 * manual value, once saved, takes precedence over the auto-produced value.
 * If user clears the manual value, the cell falls back to showing the
 * auto-produced number again.
 *
 * Status legend (left border colour):
 *   - emerald  : actual >= 95% of allocated
 *   - amber    : actual 60-94% of allocated
 *   - rose     : actual < 60% of allocated
 *   - slate    : no allocated work this day
 *   - blue     : manual override active
 *   - gray-50  : weekend (no expected work)
 */
import { useState, useEffect, useRef } from 'react';

function fmtHours(h) {
  if (h == null || h === 0) return '0';
  return Number(h).toFixed(1).replace(/\.0$/, '');
}

export default function DailyHoursCell({
  mechanicId,
  day,
  manualHours = 0,
  actualHours = 0,
  allocatedHours = 0,
  isWeekend = false,
  onCommit,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const isOverridden = manualHours > 0;
  // The number shown as "actual" — manual wins when present, else live
  // produced hours from time_log
  const displayedActual = isOverridden ? manualHours : actualHours;
  const hasAlloc = allocatedHours > 0;
  const hasAny = displayedActual > 0 || hasAlloc;

  // Variance vs allocated — only meaningful when allocated > 0
  let variancePct = null;
  let tier = 'slate';
  if (hasAlloc) {
    variancePct = Math.round((displayedActual / allocatedHours) * 100);
    if (variancePct >= 95) tier = 'emerald';
    else if (variancePct >= 60) tier = 'amber';
    else tier = 'rose';
  }
  if (isOverridden) tier = 'blue';
  if (isWeekend && !hasAny) tier = 'weekend';

  const borderColors = {
    emerald: 'border-l-emerald-500 bg-emerald-50/40',
    amber: 'border-l-amber-500 bg-amber-50/40',
    rose: 'border-l-rose-500 bg-rose-50/40',
    blue: 'border-l-blue-500 bg-blue-50/40',
    slate: 'border-l-slate-200 bg-white',
    weekend: 'border-l-slate-100 bg-slate-50',
  };

  const startEdit = () => {
    setDraft(manualHours > 0 ? String(manualHours) : displayedActual > 0 ? String(displayedActual) : '');
    setEditing(true);
  };

  const commit = async () => {
    const trimmed = (draft ?? '').trim();
    const next = trimmed === '' ? 0 : parseFloat(trimmed) || 0;
    setEditing(false);
    // Only fire the callback when the value actually changes
    if (Math.abs(next - manualHours) > 0.01) {
      await onCommit?.(next);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
  };

  if (editing) {
    return (
      <td
        className={`border-l-4 ${borderColors[tier]} border-r border-y border-slate-100 p-0`}
        data-testid={`daily-cell-${mechanicId}-${day}-edit`}
      >
        <input
          ref={inputRef}
          type="number"
          step="0.1"
          min="0"
          max="24"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKey}
          className="w-full h-12 text-center text-sm font-bold bg-blue-50 border-2 border-blue-500 outline-none rounded"
          data-testid={`daily-cell-input-${mechanicId}-${day}`}
        />
      </td>
    );
  }

  // Idle (non-edit) — clickable
  return (
    <td
      className={`border-l-4 ${borderColors[tier]} border-r border-y border-slate-100 p-0 cursor-pointer hover:ring-2 hover:ring-inset hover:ring-indigo-300 transition`}
      onClick={startEdit}
      title={hasAny
        ? `Actual ${fmtHours(displayedActual)}h / Allocated ${fmtHours(allocatedHours)}h${isOverridden ? ' · manual override' : ''}\nActual = time_log on JCs in sm_review or completed for the day.\nAllocated = quoted_labour_hours on JCs scheduled this day in any active state.\nClick to override.`
        : isWeekend ? 'Weekend — no work expected. Click to log hours.' : 'No JC activity this day. Click to log hours manually.'}
      data-testid={`daily-cell-${mechanicId}-${day}`}
    >
      <div className="px-1.5 py-1 flex flex-col items-center justify-center h-12 leading-tight">
        {hasAny ? (
          <>
            <div className="flex items-baseline gap-0.5 text-sm tabular-nums">
              <span className={`font-extrabold ${
                isOverridden ? 'text-blue-700' :
                tier === 'emerald' ? 'text-emerald-700' :
                tier === 'amber' ? 'text-amber-700' :
                tier === 'rose' ? 'text-rose-700' : 'text-slate-700'
              }`}>
                {fmtHours(displayedActual)}
              </span>
              {hasAlloc && (
                <>
                  <span className="text-slate-300 font-normal">/</span>
                  <span className="text-slate-500 font-medium text-xs">
                    {fmtHours(allocatedHours)}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 text-[9px] leading-none">
              {hasAlloc && (
                <span className={`font-bold tabular-nums ${
                  isOverridden ? 'text-blue-600' :
                  tier === 'emerald' ? 'text-emerald-700' :
                  tier === 'amber' ? 'text-amber-700' :
                  tier === 'rose' ? 'text-rose-700' : 'text-slate-500'
                }`}>
                  {variancePct}%
                </span>
              )}
              {isOverridden && (
                <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-700 font-bold uppercase tracking-wider text-[8px]" data-testid={`daily-cell-override-${mechanicId}-${day}`}>
                  M
                </span>
              )}
            </div>
          </>
        ) : (
          <span className="text-[10px] text-slate-300">—</span>
        )}
      </div>
    </td>
  );
}
